import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { cn } from "@/utils/cn";
import { formatDate } from "@/utils/format";
import { useSuperAdmin } from "../data/SuperAdminProvider";
import {
  COMPTE_STATUT_LABELS,
  COMPTE_TYPE_CONFIG,
  getFormule,
  utilisateurRoleLabel,
  type AuditActionKey,
  type Compte,
  type CompteStatut,
  type CompteType,
} from "../data/types";
import CompteConsultModal, { type ConsultTab } from "./CompteConsultModal";
import CompteForm from "./CompteForm";
import CompteRelations from "./CompteRelations";
import ConfirmationModal from "./ConfirmationModal";
import StatutBadge from "./StatutBadge";
import SupprimerCompteModal from "./SupprimerCompteModal";

interface StatutAction {
  statut: CompteStatut;
  actionKey: AuditActionKey;
  label: string;
  icon: string;
  destructive: boolean;
  description: string;
}

function buildStatutActions(compte: Compte): StatutAction[] {
  const nom = compte.nom;
  const suspendre: StatutAction = {
    statut: "suspendu",
    actionKey: "suspension",
    label: "Suspendre",
    icon: "ri-pause-circle-line",
    destructive: true,
    description: `Suspendre temporairement le compte « ${nom} » ? Le compte ne pourra plus accéder à la plateforme tant qu'il n'est pas réactivé. Cette action est tracée dans le journal d'audit.`,
  };
  const desactiver: StatutAction = {
    statut: "desactive",
    actionKey: "desactivation",
    label: "Désactiver",
    icon: "ri-forbid-2-line",
    destructive: true,
    description: `Désactiver le compte « ${nom} » ? Le compte sera bloqué jusqu'à une réactivation par un administrateur. Cette action est tracée dans le journal d'audit.`,
  };
  const reactiver: StatutAction = {
    statut: "actif",
    actionKey: "reactivation",
    label: "Réactiver",
    icon: "ri-restart-line",
    destructive: false,
    description: `Réactiver le compte « ${nom} » et lui redonner accès à la plateforme ?`,
  };
  const activer: StatutAction = {
    statut: "actif",
    actionKey: "activation",
    label: "Activer",
    icon: "ri-checkbox-circle-line",
    destructive: false,
    description: `Activer le compte « ${nom} » ? Le compte pourra se connecter immédiatement.`,
  };

  switch (compte.statut) {
    case "actif":
      return [suspendre, desactiver];
    case "en_attente":
      return [activer, desactiver];
    case "suspendu":
      return [reactiver, desactiver];
    default:
      return [reactiver];
  }
}

const CONSULT_ACTIONS: { tab: ConsultTab; label: string; icon: string }[] = [
  { tab: "abonnement", label: "Consulter l'abonnement", icon: "ri-vip-diamond-line" },
  { tab: "paiements", label: "Consulter les paiements", icon: "ri-hand-coin-line" },
  { tab: "activite", label: "Consulter l'activité", icon: "ri-history-line" },
  { tab: "audit", label: "Journal d'audit", icon: "ri-shield-check-line" },
];

interface CompteDetailViewProps {
  type: CompteType;
}

export default function CompteDetailView({ type }: CompteDetailViewProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getCompte, changeStatut, recharger } = useSuperAdmin();
  const config = COMPTE_TYPE_CONFIG[type];
  const compte = id ? getCompte(id) : undefined;

  const [editOpen, setEditOpen] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);
  const [consultTab, setConsultTab] = useState<ConsultTab>("abonnement");
  const [pendingAction, setPendingAction] = useState<StatutAction | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (!compte) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to={config.basePath}
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour aux {config.label.toLowerCase()}
        </Link>
        <EmptyState
          icon="ri-account-circle-line"
          title={`${config.singular} introuvable`}
          description="Ce compte n'existe pas ou n'est plus disponible. Retournez à la liste pour le retrouver."
          action={
            <Link
              to={config.basePath}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir tous les {config.label.toLowerCase()}
            </Link>
          }
        />
      </div>
    );
  }

  const formule = getFormule(compte.formuleId);
  const initiales = compte.nom
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
  const statutActions = buildStatutActions(compte);
  const { rattachements } = compte;

  const infos: { label: string; value: string; icon: string }[] = [
    { label: "Email", value: compte.email || "—", icon: "ri-mail-line" },
    { label: "Téléphone", value: compte.telephone || "—", icon: "ri-phone-line" },
    { label: "Ville", value: compte.ville || "—", icon: "ri-map-pin-2-line" },
  ];
  if (type === "agence") {
    infos.push({ label: "Responsable", value: compte.responsable || "—", icon: "ri-user-line" });
  }
  if (type === "utilisateur") {
    infos.push({ label: "Rôle", value: utilisateurRoleLabel(compte.role), icon: "ri-briefcase-line" });
  }
  if (type !== "utilisateur") {
    infos.push({ label: "Formule", value: formule ? formule.nom : "Aucune", icon: "ri-vip-diamond-line" });
  }
  infos.push({ label: "Compte créé le", value: formatDate(compte.createdAt), icon: "ri-calendar-line" });
  infos.push({ label: "Dernière activité", value: formatDate(compte.derniereActivite, true), icon: "ri-time-line" });

  const stats: { label: string; value: number; icon: string; tone: "primary" | "accent" | "secondary" | "neutral" }[] =
    type === "agence"
      ? [
          { label: "Propriétaires", value: rattachements.proprietaires.length, icon: "ri-user-star-line", tone: "primary" },
          { label: "Immeubles", value: rattachements.immeubles.length, icon: "ri-building-2-line", tone: "secondary" },
          { label: "Biens", value: rattachements.biens.length, icon: "ri-home-4-line", tone: "accent" },
          { label: "Locataires", value: rattachements.locataires.length, icon: "ri-user-3-line", tone: "neutral" },
          { label: "Employés", value: rattachements.employes.length, icon: "ri-team-line", tone: "primary" },
        ]
      : type === "proprietaire"
        ? [
            { label: "Immeubles", value: rattachements.immeubles.length, icon: "ri-building-2-line", tone: "primary" },
            { label: "Biens", value: rattachements.biens.length, icon: "ri-home-4-line", tone: "secondary" },
            { label: "Locataires", value: rattachements.locataires.length, icon: "ri-user-3-line", tone: "accent" },
            { label: "Contrats", value: rattachements.contrats.length, icon: "ri-file-text-line", tone: "neutral" },
          ]
        : [];

  const openConsult = (tab: ConsultTab) => {
    setConsultTab(tab);
    setConsultOpen(true);
  };

  const confirmPending = () => {
    if (!pendingAction) return;
    changeStatut(compte.id, pendingAction.statut, pendingAction.actionKey);
    setPendingAction(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <Link
        to={config.basePath}
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour aux {config.label.toLowerCase()}
      </Link>

      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-lg font-semibold text-primary-800">
            {initiales || "?"}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <StatutBadge statut={compte.statut} />
              <span className="inline-flex items-center gap-1.5 rounded-full border border-background-200 bg-background-100 px-2.5 py-1 text-xs font-label font-medium text-foreground-700">
                <i className={cn(config.icon, "text-[13px]")} aria-hidden="true" />
                {config.singular}
              </span>
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">{compte.nom}</h2>
            <p className="mt-1 text-sm text-foreground-600">
              {type === "agence"
                ? compte.responsable || "Responsable non renseigné"
                : type === "utilisateur"
                  ? utilisateurRoleLabel(compte.role)
                  : compte.email || "Email non renseigné"}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row lg:shrink-0">
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-edit-line text-base" aria-hidden="true" />
            Modifier
          </button>
          {statutActions.map((action) => (
            <button
              key={action.actionKey}
              type="button"
              onClick={() => setPendingAction(action)}
              className={cn(
                "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm font-label font-medium transition-colors cursor-pointer",
                action.destructive
                  ? "border-primary-300 bg-background-50 text-primary-700 hover:bg-primary-50"
                  : "border-secondary-300 bg-secondary-50 text-secondary-800 hover:bg-secondary-100"
              )}
            >
              <i className={cn(action.icon, "text-base")} aria-hidden="true" />
              {action.label}
            </button>
          ))}
          {compte.role !== "super_admin" && (
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
            >
              <i className="ri-delete-bin-6-line text-base" aria-hidden="true" />
              Supprimer le compte
            </button>
          )}
        </div>
      </section>

      {stats.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {stats.map((stat) => (
            <StatCard
              key={stat.label}
              icon={stat.icon}
              label={stat.label}
              value={String(stat.value)}
              tone={stat.tone}
            />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
              <i className="ri-information-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
              Informations du compte
            </h3>
          </header>
          <div className="p-4 md:p-5">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {infos.map((info) => (
                <div key={info.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                    <i className={cn(info.icon, "text-base")} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-500">{info.label}</dt>
                    <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                  </div>
                </div>
              ))}
            </dl>
            {compte.notes && (
              <div className="mt-5 border-t border-background-200 pt-4">
                <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                  Notes internes
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{compte.notes}</p>
              </div>
            )}
          </div>
        </section>

        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-search-eye-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Consulter</h3>
          </header>
          <div className="flex flex-col gap-1 p-2">
            {CONSULT_ACTIONS.map((action) => (
              <button
                key={action.tab}
                type="button"
                onClick={() => openConsult(action.tab)}
                className="flex items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-background-100 cursor-pointer"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-600">
                  <i className={cn(action.icon, "text-base")} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-label font-medium text-foreground-950">
                  {action.label}
                </span>
                <i className="ri-arrow-right-s-line shrink-0 text-base text-foreground-400" aria-hidden="true" />
              </button>
            ))}
          </div>
        </section>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
            <i className="ri-links-line text-base" aria-hidden="true" />
          </span>
          <h3 className="font-heading text-base font-semibold text-foreground-950">
            {type === "agence" ? "Éléments rattachés à l'agence" : "Éléments rattachés"}
          </h3>
        </div>
        <CompteRelations compte={compte} />
      </div>

      <CompteForm open={editOpen} onClose={() => setEditOpen(false)} type={type} compte={compte} />

      <CompteConsultModal
        open={consultOpen}
        onClose={() => setConsultOpen(false)}
        compte={compte}
        initialTab={consultTab}
      />

      <ConfirmationModal
        open={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmPending}
        title={pendingAction ? `${pendingAction.label} le compte` : ""}
        description={pendingAction?.description ?? ""}
        confirmLabel={pendingAction?.label ?? "Confirmer"}
        icon={pendingAction?.icon}
        tone={pendingAction?.destructive ? "danger" : "primary"}
      />

      <SupprimerCompteModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        compteId={compte.id}
        nom={compte.nom}
        email={compte.email}
        typeLabel={config.singular}
        onDeleted={() => {
          void recharger();
          navigate(config.basePath);
        }}
      />
    </div>
  );
}